import { makeVar } from '@apollo/client';
import { Box, InputAdornment, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';
import { SetOptional } from 'type-fest';

import { Button, ButtonProps } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import {
  FormAwareTypeaheadSelectInput,
  FormAwareTypeaheadSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { useValidateOnChange } from '@/components/form/formAwareInputs/hooks/useValidateOnChange';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Settings04Icon } from '@/components/icons/Settings04Icon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { User01Icon } from '@/components/icons/User01Icon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { useDPFormSuggestionsContext } from '@/modules/aiDP/context/dpFormSuggestions.context';
import { LocatableFormAwareInput } from '@/modules/aiSuggestions/components/forms/LocatableFormAwareInput';
import { ContextualHelpTrigger } from '@/modules/content/components/ContextualHelpTrigger';
import { hasOnlyInsurancePolicies } from '@/modules/dispositiveProvisions/dispositiveProvisions.formatting';
import {
  CombinedFormShape,
  CombinedPaths,
} from '@/modules/dispositiveProvisions/dispositiveProvisions.types';
import {
  DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useIRSConstants } from '@/modules/irs/useIRSConstants';
import { COLORS } from '@/styles/tokens/colors';
import { MUI_INPUT_HEIGHT_PX } from '@/styles/tokens/inputHeights';
import {
  DispositiveProvisionDispositionKind,
  DispositiveProvisionTemplateKind,
  TransferMethod,
} from '@/types/schema';

import { useDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import { useDispositiveProvisionsTemplateSplitScreenModalContext } from '../../DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal.context';
import { RecipientSelectorModal } from '../../RecipientSelectorModal';
import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '../DispositiveProvisionsForm.constants';
import {
  dispositionKindOptions,
  getTransferTaxKindOptions,
  otherDispositionKindOptions,
} from '../DispositiveProvisionsForm.constants';
import { DispositiveProvisionsFormRecipient } from '../DispositiveProvisionsForm.types';
import {
  useRecipientOptions,
  useRecipientOptionsParams,
} from '../hooks/useRecipientOptions';
import { useRemainingExemptionAmountForClient } from '../hooks/useRemainingExemptionAmountForClient';
import {
  useDeathOrderAwareClient,
  useHasGrantorWithStateTax,
} from '../hooks/utilityHooks';
import { AdvancedSettingsCard } from './AdvancedSettingsCard';
import { AssetDistributionCallout } from './AssetDistributionCallout';
import { AssetSelectorDropdown } from './AssetSelectorDropdown';
import { BearsTaxToggle } from './BearsTaxToggle';
import { DispositiveProvisionsRecipientTemplateRecipientConfiguration } from './DispositiveProvisionsRecipient.types';
import {
  getDispositionKindOption,
  getDispositionKindsToDisable,
  getTemplateRulesForTemplateType,
  validateRecipient,
} from './DispositiveProvisionsRecipient.utils';
import { DistributionSummaryCallout } from './DistributionSummaryCallout';
import {
  useDefaultBearsTax,
  useTemplateRecipientBearsTax,
} from './hooks/useDefaultBearsTax';
import { useDefaultRecipientTransferTaxKind } from './hooks/useDefaultRecipientTransferTaxKind';
import { useSetRecipientField } from './hooks/useSetRecipientField';
import { useSyncRecipientKind } from './hooks/useSyncRecipientKind';

export const showStateTaxContentVar = makeVar<boolean>(false);

export type RecipientsBasePath =
  | `${typeof DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients`
  | `${typeof DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.firstDeath.recipients`
  | `${typeof DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.secondDeath.recipients`
  // BDG reuses the asset selector with its own recipient namespace.
  | 'beneficiaryDesignations.primary.recipients'
  | 'beneficiaryDesignations.contingent.recipients';

interface DispositiveProvisionsRecipientProps {
  idx: number;
  /** this will be null for templates, which are death order agnostic by definition */
  deadPrimaryClientId: string | null;
  isSecondDeath: boolean;
  onRemove?: ((index: number) => void) | null;
  templateType?: DispositiveProvisionTemplateKind;
  recipientConfiguration?: Partial<DispositiveProvisionsRecipientTemplateRecipientConfiguration>;
  emptyRecipientOptionDisplay?: string;
  isOptional?: boolean;
  /**
   * This field gets passed down from the PrimaryClientDeathSectionRecipients
   * component.
   */
  deathEvent?: PrimaryClientDeathEvent;
}

export function DispositiveProvisionsRecipient({
  idx,
  deathEvent,
  deadPrimaryClientId,
  isSecondDeath,
  onRemove,
  templateType,
  recipientConfiguration: externalRecipientConfiguration,
  emptyRecipientOptionDisplay,
  isOptional,
}: DispositiveProvisionsRecipientProps) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const { availableStateEstateTaxes } = useIRSConstants();
  const { control, setFocus } = useFormContext<CombinedFormShape>();
  const { isTemplateMode } =
    useDispositiveProvisionsTemplateSplitScreenModalContext();
  const {
    householdId: dispositiveProvisionsContextHouseholdId,
    primaryClients,
    isTwoClientHousehold,
  } = useDispositiveProvisionsContext();
  // this is necessary because the recipient picker can be invoked outside of a waterfall/death order
  const { householdId: householdContextId } = useHouseholdDetailsContext();
  const { entityId } = useEntityDetailsContext();
  const householdId =
    householdContextId ?? dispositiveProvisionsContextHouseholdId;

  const firstDeadGrantor = useDeathOrderAwareClient(
    deathEvent ? { deathEvent } : undefined
  );
  const hasGrantorWithStateTax = useHasGrantorWithStateTax();

  const { templateRules, recipientConfiguration } =
    getTemplateRulesForTemplateType(
      templateType,
      externalRecipientConfiguration
    );
  const { templateRequiresRecipient } = templateRules;

  const { dpSuggestionKinds } = useDPFormSuggestionsContext();

  const recipientsBasePath: RecipientsBasePath =
    deathEvent !== undefined
      ? (`${getDeathEventBasePath(deathEvent)}.recipients` as const)
      : (`${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const);

  const setRecipientField = useSetRecipientField(recipientsBasePath, idx);

  useValidateOnChange<CombinedFormShape>(
    true,
    `${recipientsBasePath}.${idx}.recipientId` as const satisfies CombinedPaths,
    control,
    {
      valueType: 'string',
    }
  );

  const selectedDistributionKind = useWatch({
    control,
    name: `${recipientsBasePath}.${idx}.dispositionKind_ButtonGroup` as const satisfies CombinedPaths,
  });

  const selectedFormulaKind = useWatch({
    control,
    name: `${recipientsBasePath}.${idx}.dispositionKind_Select` as const satisfies CombinedPaths,
  });

  const notes = useWatch({
    control,
    name: `${recipientsBasePath}.${idx}.notes` as const satisfies CombinedPaths,
  });

  const recipientsFromForm = useWatch({
    control,
    name: recipientsBasePath,
  }) as unknown as DispositiveProvisionsFormRecipient[] | undefined;

  const [notesAreExpanded, setNotesAreExpanded] = useState(
    Boolean(notes) || templateRules.alwaysShowNotes
  );

  const [advancedSettingsIsOpen, setAdvancedSettingsIsOpen] = useState(false);

  const advancedSettingsButton = (
    <IconButton
      icon={Settings04Icon}
      ariaLabel="Advanced settings"
      size="sm"
      variant="transparent"
      onClick={() => setAdvancedSettingsIsOpen(!advancedSettingsIsOpen)}
      sx={{
        height: MUI_INPUT_HEIGHT_PX,
        width: MUI_INPUT_HEIGHT_PX,
        backgroundColor: advancedSettingsIsOpen
          ? COLORS.NAVY[100]
          : 'transparent',
        '&:hover': {
          backgroundColor: COLORS.NAVY[100],
          borderColor: 'transparent',
        },
      }}
    />
  );

  const recipients: DispositiveProvisionsFormRecipient[] = useMemo(() => {
    return recipientsFromForm ?? [];
  }, [recipientsFromForm]);

  const thisRecipient = recipients[idx];

  const requiredFields = useMemo<CombinedPaths[]>(() => {
    if (thisRecipient?.recipientId) {
      const paths: CombinedPaths[] = [
        `${recipientsBasePath}.${idx}.recipientId` as const satisfies CombinedPaths,
        `${recipientsBasePath}.${idx}.dispositionKind_Select` as const satisfies CombinedPaths,
        `${recipientsBasePath}.${idx}.transferTaxKind` as const satisfies CombinedPaths,
      ];
      return paths;
    }
    return [];
  }, [idx, recipientsBasePath, thisRecipient?.recipientId]);

  const isAmountDistributionKind =
    selectedDistributionKind === DispositiveProvisionDispositionKind.Amount;
  const isPercentDistributionKind =
    selectedDistributionKind === DispositiveProvisionDispositionKind.Percentage;
  const isFormulaDistributionKind = selectedDistributionKind === 'other';
  const isAssetDistributionKind = selectedDistributionKind === 'asset';

  const showAdvancedSettingsCard =
    isPercentDistributionKind ||
    isAmountDistributionKind ||
    isFormulaDistributionKind ||
    isAssetDistributionKind;

  const deadPrimaryClientIds = useMemo(() => {
    if (!isSecondDeath) {
      return compact([deadPrimaryClientId]);
    }
    return primaryClients.map((c) => c.id);
  }, [deadPrimaryClientId, primaryClients, isSecondDeath]);

  const recipientOptionsParams = useRecipientOptionsParams({ deathEvent });
  const { options: recipientOptions, typeaheadOptions } = useRecipientOptions(
    recipientOptionsParams,
    recipientConfiguration
  );

  const hasStateTax = useMemo(() => {
    if (isTemplateMode) {
      return hasGrantorWithStateTax;
    }
    if (!firstDeadGrantor) return false;

    return Boolean(
      availableStateEstateTaxes?.includes(firstDeadGrantor.stateCode!)
    );
  }, [
    availableStateEstateTaxes,
    firstDeadGrantor,
    hasGrantorWithStateTax,
    isTemplateMode,
  ]);

  useEffect(() => {
    if (hasStateTax) {
      showStateTaxContentVar(true);
    } else {
      showStateTaxContentVar(false);
    }
  }, [hasStateTax]);

  useDefaultRecipientTransferTaxKind(
    thisRecipient,
    recipientsBasePath,
    idx,
    hasStateTax
  );
  useDefaultBearsTax(thisRecipient, idx, templateType);
  useTemplateRecipientBearsTax(idx, templateType);

  // selectableRecipientOptions *does* include disabled recipients, but just doesn't include
  // the component section label elements of the list
  const selectableRecipientOptions = useMemo(() => {
    return recipientOptions.filter(
      (o) => !('component' in o)
    ) as SelectInputOption[];
  }, [recipientOptions]);

  const { data: clientExemptionData } = useRemainingExemptionAmountForClient({
    // clientProfileId of who died
    clientId: deadPrimaryClientId,
  });

  const [isRecipientSelectorModalOpen, setIsRecipientSelectorModalOpen] =
    useState(false);
  const handleClose = useCallback(() => {
    setIsRecipientSelectorModalOpen(false);
  }, []);

  const dispositionOptionsToDisable = useMemo(
    () => getDispositionKindsToDisable(recipients),
    [recipients]
  );

  const dispositionKindSelectOptions = useMemo(
    () =>
      otherDispositionKindOptions.flatMap((option) => {
        if (
          option.value ===
          DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption
        ) {
          // if there's no state tax, don't show the option
          if (!hasStateTax) {
            return [];
          }
        }
        if (!hasStateTax && !isTemplateMode) {
          if (
            [
              DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption,
              DispositiveProvisionDispositionKind.RemainingStateExemption,
            ].includes(option.value)
          ) {
            return [];
          }
        }

        return getDispositionKindOption(
          option,
          clientExemptionData,
          dispositionOptionsToDisable,
          isTemplateMode
        );
      }),
    [
      clientExemptionData,
      dispositionOptionsToDisable,
      hasStateTax,
      isTemplateMode,
    ]
  );

  function handleExpandNotes() {
    setNotesAreExpanded(true);
    // allow a loop for the notes to expand
    setTimeout(() => {
      setFocus(
        `${recipientsBasePath}.${idx}.notes` as const satisfies CombinedPaths
      );
    });
  }

  let isRecipientRequired =
    requiredFields.includes(
      `${recipientsBasePath}.${idx}.recipientId` as const satisfies CombinedPaths
    ) || templateRequiresRecipient;
  if (isOptional) {
    isRecipientRequired = false;
  }

  // When switching distribution kind to "Asset", set defaults if no existing values
  const prevDistributionKind = usePrevious(selectedDistributionKind);
  useEffect(() => {
    if (prevDistributionKind === selectedDistributionKind) return;
    if (prevDistributionKind === undefined) return;

    if (
      selectedDistributionKind === 'asset' &&
      prevDistributionKind !== 'asset'
    ) {
      // Switching TO asset: only set defaults when no existing values are present
      if (thisRecipient?.dispositionAssetPercentage == null) {
        setRecipientField('dispositionAssetPercentage', new Decimal(100), {
          shouldDirty: true,
        });
      }
      // Asset DPs always transfer in kind; force it on switch in case the
      // recipient was previously set to liquidate
      setRecipientField('transferMethod', TransferMethod.InKind, {
        shouldDirty: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when distribution kind changes
  }, [selectedDistributionKind]);

  // For asset DPs: detect when only insurance policies are selected (hides transfer toggle)
  const isInsuranceOnlySelection = useMemo(() => {
    if (!isAssetDistributionKind || !thisRecipient) return false;
    return hasOnlyInsurancePolicies({
      insurancePolicies: thisRecipient.insurancePolicyIds?.length ?? 0,
      assetGroups: thisRecipient.assetGroupIds?.length ?? 0,
      assetStatics: thisRecipient.assetStaticIds?.length ?? 0,
      ownershipStakes: thisRecipient.ownershipStakeIds?.length ?? 0,
    });
  }, [isAssetDistributionKind, thisRecipient]);

  // Templates don't support asset-level DPs.
  const filteredDispositionKindOptions = useMemo(() => {
    if (!isTemplateMode) return dispositionKindOptions;
    return dispositionKindOptions.filter((o) => o.value !== 'asset');
  }, [isTemplateMode]);

  useSyncRecipientKind({
    currentRecipient: thisRecipient,
    recipientsBasePath,
    idx,
  });

  return (
    <>
      <RecipientSelectorModal
        handleClose={handleClose}
        isOpen={isRecipientSelectorModalOpen}
        householdId={householdId}
        deadPrimaryClientId={deadPrimaryClientId}
        deadPrimaryClientIds={deadPrimaryClientIds}
        isTwoClientHousehold={isTwoClientHousehold}
        recipientConfiguration={recipientConfiguration}
        onRecipientCreated={(newRecipientId) =>
          setRecipientField('recipientId', newRecipientId, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
      />
      <Box data-testid={`DispositiveProvisionsRecipient-${idx}`} key={idx}>
        <FormLayoutRow>
          <FormLayoutItem width={7}>
            <LocatableFormAwareInput<
              CombinedFormShape,
              FormAwareTypeaheadSelectInputProps<CombinedFormShape>
            >
              FormAwareElement={FormAwareTypeaheadSelectInput}
              suggestionKind={
                dpSuggestionKinds?.aiSuggestionKind
                  ? dpSuggestionKinds.aiSuggestionKind
                  : null
              }
              control={control}
              label="Recipient"
              addNewOption={
                !disabledFromContext &&
                recipientConfiguration.createRecipientTypes.length
                  ? {
                      onClick: () => setIsRecipientSelectorModalOpen(true),
                    }
                  : undefined
              }
              fieldName={
                `${recipientsBasePath}.${idx}.recipientId` as const satisfies CombinedPaths
              }
              options={typeaheadOptions}
              groupBy={(option) => option.groupName ?? ''}
              placeholder={emptyRecipientOptionDisplay ?? 'Select recipient'}
              hideEmptyOption
              // Recipient cards are draggable list items (position: relative;
              // z-index), so each is its own stacking context. Portal the popper
              // to the body so it isn't painted under sibling recipient cards.
              disablePortal={false}
              // Adding a recipient auto-focuses this input; keep the menu closed
              // on that focus so the row expands without the dropdown popping
              // open (the user clicks or types to open it).
              openOnFocus={false}
              testId={`select-${recipientsBasePath}.${idx}.recipientId`}
              required={isRecipientRequired}
              validation={{
                mustBeValidOption: (value) => {
                  if (!value || typeof value !== 'string') return undefined;
                  return validateRecipient(value, selectableRecipientOptions);
                },
              }}
              startAdornment={
                <InputAdornment position="start">
                  <User01Icon size={16} />
                </InputAdornment>
              }
            />
          </FormLayoutItem>
          <FormLayoutItem width={5}>
            <Stack spacing={1}>
              <FormAwareSelectInput<CombinedFormShape>
                control={control}
                label="Recipient type"
                fieldName={
                  `${recipientsBasePath}.${idx}.transferTaxKind` as const satisfies CombinedPaths
                }
                options={getTransferTaxKindOptions({
                  hasStateTax,
                  recipientConfiguration,
                })}
                required={requiredFields.includes(
                  `${recipientsBasePath}.${idx}.transferTaxKind` as const satisfies CombinedPaths
                )}
              />
              <ContextualHelpTrigger
                display="Which type should I pick?"
                contextualHelpId="dispositiveProvisionsRecipientType"
                variant="helpText"
                {...(entityId && { contextData: { entityId } })}
              />
            </Stack>
          </FormLayoutItem>
        </FormLayoutRow>
        <FormLayoutRow
          sx={{
            alignItems: 'end',
            marginBottom: 1,
          }}
        >
          <FormLayoutItem width={4}>
            <FormAwareButtonGroup<CombinedFormShape>
              label="Distribution type"
              fieldName={
                `${recipientsBasePath}.${idx}.dispositionKind_ButtonGroup` as const satisfies CombinedPaths
              }
              control={control}
              options={filteredDispositionKindOptions}
              variant="light"
            />
          </FormLayoutItem>
          <FormLayoutItem width={8}>
            {(isPercentDistributionKind || isAmountDistributionKind) && (
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <Box sx={{ flex: 1 }}>
                  {isPercentDistributionKind && (
                    <FormAwarePercentInput<CombinedFormShape>
                      label="Distribution percentage"
                      fieldName={
                        `${recipientsBasePath}.${idx}.dispositionPercentage` as const satisfies CombinedPaths
                      }
                      control={control}
                      isDecimalJSInput
                      decimalScale={5}
                      fixedDecimalScale={false}
                    />
                  )}
                  {isAmountDistributionKind && (
                    <FormAwareCurrencyInput<CombinedFormShape>
                      label="Distribution amount"
                      fieldName={
                        `${recipientsBasePath}.${idx}.dispositionAmount` as const satisfies CombinedPaths
                      }
                      control={control}
                      isDecimalJSInput
                    />
                  )}
                </Box>
                {advancedSettingsButton}
              </Stack>
            )}
            {selectedDistributionKind === 'other' && (
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormAwareSelectInput<CombinedFormShape>
                    label="Distribution formula"
                    fieldName={
                      `${recipientsBasePath}.${idx}.dispositionKind_Select` as const satisfies CombinedPaths
                    }
                    control={control}
                    options={dispositionKindSelectOptions}
                    required={requiredFields.includes(
                      `${recipientsBasePath}.${idx}.dispositionKind_Select` as const satisfies CombinedPaths
                    )}
                  />
                </Box>
                {advancedSettingsButton}
              </Stack>
            )}
            {isAssetDistributionKind && (
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <Box sx={{ flex: 1 }}>
                  <AssetSelectorDropdown
                    recipientsBasePath={recipientsBasePath}
                    idx={idx}
                  />
                </Box>
                {advancedSettingsButton}
              </Stack>
            )}
          </FormLayoutItem>
        </FormLayoutRow>
        {showAdvancedSettingsCard && (
          <AdvancedSettingsCard
            recipientsBasePath={recipientsBasePath}
            idx={idx}
            isExpanded={advancedSettingsIsOpen}
            dispositionKind={selectedDistributionKind}
            showFormulaPercentage={isFormulaDistributionKind}
            selectedFormulaKind={selectedFormulaKind}
            hideTransferToggle={isInsuranceOnlySelection}
          >
            <BearsTaxToggle
              deathEvent={deathEvent}
              recipientsBasePath={recipientsBasePath}
              idx={idx}
              templateType={templateType}
              bare
            />
          </AdvancedSettingsCard>
        )}
        {showAdvancedSettingsCard && !isAssetDistributionKind && (
          <DistributionSummaryCallout
            distributionKind={getDistributionKindLabel({
              isAmountDistributionKind,
              isPercentDistributionKind,
            })}
            dispositionAmount={thisRecipient?.dispositionAmount}
            dispositionPercentage={thisRecipient?.dispositionPercentage}
            dispositionFormulaicPercentage={
              thisRecipient?.dispositionFormulaicPercentage
            }
            dispositionLimit={thisRecipient?.dispositionLimit}
            formulaKind={selectedFormulaKind}
            bearsTax={thisRecipient?.bearsTax}
            growByInflation={thisRecipient?.growByInflation}
          />
        )}
        {isAssetDistributionKind && (
          <AssetDistributionCallout
            recipientsBasePath={recipientsBasePath}
            idx={idx}
          />
        )}
        {/* When the advanced settings card is shown, BearsTaxToggle is rendered inside it */}
        {!showAdvancedSettingsCard && (
          <BearsTaxToggle
            deathEvent={deathEvent}
            recipientsBasePath={recipientsBasePath}
            idx={idx}
            templateType={templateType}
          />
        )}
        <FormLayoutRow sx={{ display: notesAreExpanded ? 'initial' : 'none' }}>
          <FormLayoutItem width={12}>
            <FormAwareTextInput<CombinedFormShape>
              label="Notes"
              multiline
              fieldName={
                `${recipientsBasePath}.${idx}.notes` as const satisfies CombinedPaths
              }
              control={control}
              rows={3}
              resizable
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          mt={2}
        >
          <Box>
            {!notesAreExpanded && (
              <FooterButton
                variant="transparent"
                onClick={() => handleExpandNotes()}
                disabled={disabledFromContext}
                startIcon={PlusIcon}
              >
                Add notes
              </FooterButton>
            )}
          </Box>
          {onRemove && !disabledFromContext && (
            <Box>
              <FooterButton
                variant="destructive-transparent"
                disabled={disabledFromContext}
                startIcon={Trash01Icon}
                onClick={() => {
                  onRemove?.(idx);
                }}
              >
                Remove recipient
              </FooterButton>
            </Box>
          )}
        </Stack>
      </Box>
    </>
  );
}

function getDistributionKindLabel({
  isAmountDistributionKind,
  isPercentDistributionKind,
}: {
  isAmountDistributionKind: boolean;
  isPercentDistributionKind: boolean;
}): 'amount' | 'percentage' | 'formula' {
  if (isAmountDistributionKind) return 'amount';
  if (isPercentDistributionKind) return 'percentage';
  return 'formula';
}

function FooterButton(props: SetOptional<ButtonProps, 'size'>) {
  return <Button size="sm" {...props} />;
}
